import { useParams } from "#src/Routers/hooks";
import {
  useInvestigation,
  useInvestigationMessages,
  usePostInvestigationMessage,
  useSubmitInvestigationApproval,
} from "#src/components/hooks/adapters/useInvestigations";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { ConversationFeed } from "#src/routes/investigations/[investigationId]/ConversationFeed";
import { FollowUpInput } from "#src/routes/investigations/[investigationId]/FollowUpInput";
import {
  useConversationAutoScroll,
  useFlashHighlightOnNewMessage,
} from "#src/routes/investigations/[investigationId]/InvestigationDetailPage.helpers";
import {
  type InvestigationDetailPageParameters,
  InvestigationDetailRoutePath,
} from "#src/routes/investigations/[investigationId]/index";
import { InvestigationErrorBlock } from "#src/routes/investigations/[investigationId]/InvestigationErrorBlock";
import { ProgressTicker } from "#src/routes/investigations/[investigationId]/ProgressTicker";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  flattenMessagesOldestFirst,
  InvestigationStatusLabel,
  InvestigationStatusToPillVariantMapping,
  isAgentBusy,
  pairPendingApprovals,
} from "#src/view-models/investigations";
import { Page, Panel, Pill, Skeleton } from "@validereinc/common-components";
import {
  ApprovalDecision,
  InvestigationMessageRole,
  InvestigationStatus,
} from "@validereinc/domain";
import { FeatureFlags } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

export const InvestigationDetailPage = () => {
  const { investigationId } = useParams<InvestigationDetailPageParameters>();
  const [breadcrumbs] = useBreadcrumbsFromRoute(InvestigationDetailRoutePath);
  const { data, isLoading } = useInvestigation(investigationId);

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    isFetchingNextPage: isLoadingOlderMessages,
    hasNextPage: hasOlderMessages,
    fetchNextPage: fetchOlderMessages,
  } = useInvestigationMessages(investigationId);

  // Memoized so message identity is stable across polls when the underlying pages haven't changed.
  const messages = useMemo(
    () => flattenMessagesOldestFirst(messagesData?.pages),
    [messagesData]
  );
  const pendingApprovalPairing = useMemo(
    () => pairPendingApprovals(messages),
    [messages]
  );
  const [isApprovalAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.StratosExternalApiRegistry,
  });
  const hasOpenPendingApproval =
    Boolean(pendingApprovalPairing.openPendingApprovalMessage) &&
    isApprovalAvailable;
  const postMessage = usePostInvestigationMessage(investigationId ?? "");
  const submitApproval = useSubmitInvestigationApproval(investigationId ?? "");
  const isConversationSubmitting =
    postMessage.isLoading || submitApproval.isLoading;

  // Scroll-to-bottom should only fire when the newest page changes (new turn arrived) or
  // when a new progress event lands — NOT when the user clicks "load older". Track the
  // newest page's length and the total page count separately so loading older pages
  // (which only grows total page count) doesn't yank the scroll position.
  const newestPage = messagesData?.pages[0];
  const newestMessageCount = newestPage?.data.length ?? 0;
  const progressEventCount = data?.progress.length ?? 0;
  const newestMessageId = newestPage?.data[0]?.id ?? null;
  const newestAgentMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === InvestigationMessageRole.AGENT) {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  const { registerMainElement, scrollAnchorRef, hasNewMessage, jumpToLatest } =
    useConversationAutoScroll({
      newestMessageCount,
      progressEventCount,
      newestMessageId,
      newestAgentMessageId,
    });

  const highlightedMessageId = useFlashHighlightOnNewMessage(newestMessageId);

  const status = data?.status;
  const showProgressFeed = isAgentBusy(status);
  const showErrorBlock = status === InvestigationStatus.ERROR && data?.error;

  return (
    <Page
      title="Investigation"
      breadcrumbs={breadcrumbs}
      contentRef={registerMainElement}
      actionRow={
        status
          ? [
              <Pill
                key="status"
                variant={InvestigationStatusToPillVariantMapping[status]}
              >
                {InvestigationStatusLabel[status]}
              </Pill>,
            ]
          : []
      }
    >
      <div className={cx("investigation-detail")}>
        <Panel
          title="Conversation"
          isFluidY
        >
          {hasNewMessage ? (
            <button
              type="button"
              className={cx("new-message-pill")}
              onClick={jumpToLatest}
              data-testid="new-message-pill"
            >
              New message — jump to latest{" "}
              <span aria-hidden="true">&darr;</span>
            </button>
          ) : null}
          <Skeleton isLoading={isLoading || isLoadingMessages}>
            {data ? (
              <ConversationFeed
                messages={messages}
                status={data.status}
                report={data.report}
                pendingApprovalMessageId={
                  hasOpenPendingApproval
                    ? pendingApprovalPairing.openPendingApprovalMessage?.id
                    : null
                }
                resolutionsByPendingApprovalId={
                  pendingApprovalPairing.resolutionsByPendingApprovalId
                }
                isApprovalLoading={isConversationSubmitting}
                hasOlderMessages={hasOlderMessages}
                isLoadingOlderMessages={isLoadingOlderMessages}
                highlightedMessageId={highlightedMessageId}
                onApprovalDecision={(decision) => {
                  if (
                    decision !== ApprovalDecision.APPROVE &&
                    decision !== ApprovalDecision.REJECT
                  ) {
                    return;
                  }
                  submitApproval.mutate({ decision });
                }}
                onLoadOlder={() => {
                  void fetchOlderMessages();
                }}
              />
            ) : null}
          </Skeleton>
        </Panel>

        {showErrorBlock || showProgressFeed || (investigationId && status) ? (
          <div className={cx("conversation-footer")}>
            {showErrorBlock && data?.error ? (
              <InvestigationErrorBlock error={data.error} />
            ) : null}

            {showProgressFeed && data ? (
              <ProgressTicker progress={data.progress} />
            ) : null}

            {investigationId && status ? (
              <Panel
                title="Send a follow-up"
                isFluidY
              >
                <FollowUpInput
                  hasOpenPendingApproval={hasOpenPendingApproval}
                  status={status}
                  postMessage={postMessage}
                  isApprovalLoading={submitApproval.isLoading}
                />
              </Panel>
            ) : null}
          </div>
        ) : null}

        <div
          ref={scrollAnchorRef}
          className={cx("scroll-anchor")}
          aria-hidden
        />
      </div>
    </Page>
  );
};
